import React, { useState } from "react";
import { API } from "@/lib/api";
import { Upload, CheckCircle2 } from "lucide-react";
import { useLocation } from "react-router-dom";
import { useCurrency } from "@/lib/currency";
import { track } from "@/lib/analytics";

const CATEGORIES = [
  "Fasteners & hardware",
  "Textiles & hosiery accessories",
  "Furniture",
  "Electronics",
  "Medical equipment",
  "Cycle parts",
  "Yiwu general goods",
  "Other",
];
const COUNTRIES = ["India", "USA", "UK / Europe", "Middle East", "Africa", "Other"];

export default function LeadForm({ source = "enquiry", defaultCategory = "", defaultDescription = "", withFile = true, dark = false }) {
  const { pathname, search } = useLocation();
  const { currency } = useCurrency();
  const [form, setForm] = useState({
    name: "",
    company: "",
    whatsapp: "",
    email: "",
    country: "",
    category: defaultCategory,
    description: defaultDescription,
  });
  const [file, setFile] = useState(null);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || (!form.whatsapp && !form.email)) {
      setError("Please provide your name and a WhatsApp number or email.");
      setStatus("error");
      return;
    }
    setStatus("sending");
    setError("");
    try {
      let response;
      if (withFile && file) {
        const fd = new FormData();
        Object.entries({ ...form, source, currency }).forEach(([k, v]) => fd.append(k, v || ""));
        fd.append("file", file);
        response = await fetch(`${API}/leads/enquiry`, { method: "POST", body: fd });
      } else {
        response = await fetch(`${API}/leads`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ...form, source, currency }),
        });
      }
      if (!response.ok) throw new Error(response.status === 413 ? "The attachment is too large. Please choose a file under 10 MB." : "We couldn't submit your request. Your details are still here—please try again.");
      track("lead_submit", { source });
      track(source === "callback" ? "consultation_request" : search.includes("intent=verification") ? "supplier_verification_submit" : pathname === "/contact" ? "contact_submit" : "rfq_submit", { source });
      if (file) track("file_upload_complete", { source, size: file.size });
      setStatus("done");
    } catch (err) {
      setError(err.message || "We couldn't submit your request. Please try again.");
      setStatus("error");
    }
  };

  if (status === "done") {
    return (
      <div data-testid="lead-form-success" role="status" style={{ padding: 20, textAlign: "center", color: dark ? "#fff" : "var(--navy)" }}>
        <h3 style={{ fontSize: 22, marginBottom: 8 }}><CheckCircle2 size={24} style={{ margin: "0 auto 14px" }} />Requirement received</h3>
        <p style={{ color: dark ? "#CFE0F4" : "var(--grey)" }}>
          Our team will respond within one business day with feasibility, a country recommendation
          and an indicative cost band.
        </p>
      </div>
    );
  }

  return (
    <form className="enq-form" onSubmit={submit} data-testid="lead-form">
      <div className="at-form-grid">
        <label><span className="at-form-label">Your name</span><input placeholder="Your Name" autoComplete="name" required value={form.name} onChange={set("name")} data-testid="lead-name" style={inp} /></label>
        <label><span className="at-form-label">Company (optional)</span><input placeholder="Company" autoComplete="organization" value={form.company} onChange={set("company")} data-testid="lead-company" style={inp} /></label>
        <label><span className="at-form-label">WhatsApp number</span><input placeholder="Country code + number" type="tel" autoComplete="tel" value={form.whatsapp} onChange={set("whatsapp")} data-testid="lead-whatsapp" style={inp} /></label>
        <label><span className="at-form-label">Email address</span><input placeholder="you@company.com" type="email" autoComplete="email" value={form.email} onChange={set("email")} data-testid="lead-email" style={inp} /></label>
      </div>
      <select aria-label="Destination country" value={form.country} onChange={set("country")} style={inp} data-testid="lead-country">
        <option value="">Destination country</option>
        {COUNTRIES.map((c) => (
          <option key={c}>{c}</option>
        ))}
      </select>
      <select aria-label="Product category" value={form.category} onChange={set("category")} style={inp} data-testid="lead-category">
        <option value="">Product category</option>
        {CATEGORIES.map((c) => (
          <option key={c}>{c}</option>
        ))}
      </select>
      <textarea
        aria-label="Product requirements"
        placeholder="Describe the product, target quantity, specs and any certifications required..."
        value={form.description}
        onChange={set("description")}
        data-testid="lead-description"
        style={{ ...inp, minHeight: 96, resize: "vertical" }}
      />
      {withFile && (
        <label className="drop" style={{ cursor: "pointer", display: "block" }} data-testid="lead-file-drop">
          <b><Upload size={16} />Attach drawing / photo / spec sheet</b>
          {file ? file.name : "PDF, JPG, PNG, DWG, XLSX — up to 10 MB"}
          <input type="file" className="sr-only" aria-label="Attach drawing or specification"
            accept=".pdf,.jpg,.jpeg,.png,.dwg,.xlsx"
            onChange={(e) => setFile(e.target.files[0])}
            data-testid="lead-file-input"
          />
        </label>
      )}
      {status === "error" && (
        <p style={{ color: "#D62828", fontSize: 13, marginBottom: 8 }} data-testid="lead-error">
          {error}
        </p>
      )}
      <button className="btn btn-red" style={{ width: "100%", textAlign: "center" }} disabled={status === "sending"} data-testid="lead-submit">
        {status === "sending" ? "Submitting…" : "Submit Requirement"}
      </button>
    </form>
  );
}

const inp = {
  width: "100%",
  border: "1.5px solid var(--line)",
  borderRadius: 10,
  padding: "12px 13px",
  fontFamily: "inherit",
  fontSize: 14,
  marginBottom: 11,
  background: "#fff",
};
